import { isBoolean, isDefined } from "@ciaopeople/cp-shatsu/TypeChecking";
import { ItemLabelDecorator } from "../../../../gui/Common.js";
import { ControlButton } from "./ControlButton.js";
import { IconName } from "../../../../gui/IconSVG.js";
import { GenericFormatLabel, ParsedFormat } from "@mp-commons/modules/player/video/FormatAndSizePreference.js";
import { Modal, ModalContainer } from "./Modal.js";
import { DOMUtils } from "@mp-commons/lib/utils/DOMUtils.js";
import { PARTHENOPE_GLOBAL_CSS_PREFIX } from "../../ParthenopeGUIV2.js";

export type SelectItemData<A> = {
    label: {
        text: string,
        decorator?: ItemLabelDecorator
    },
    value: A,
    selected: boolean
}

class ControlSelectItem<A> {

    private mainHtml: HTMLElement;

    constructor(private data: SelectItemData<A>) {
        this.initHTML();
    }

    get html(): HTMLElement {
        return this.mainHtml;
    }

    get value(): A {
        return this.data.value;
    }

    get label(): { text: string, decorator?: ItemLabelDecorator } {
        return this.data.label;
    }

    public selected(s?: boolean): boolean {
        if (isDefined(s) && isBoolean(s)) {
            this.data.selected = s;
            if (this.data.selected) this.mainHtml.classList.add("selected");
            else this.mainHtml.classList.remove("selected");
        }
        return this.data.selected;
    }

    public onSelect(listener: (item: ControlSelectItem<A>) => void): void {
        this.mainHtml.addEventListener("click", (evt) => {
            listener(this);
        })
    }

    private initHTML() {
        const bClass = `${PARTHENOPE_GLOBAL_CSS_PREFIX}-select__item` +
            (this.data.label.decorator ? ` ${this.data.label.decorator}` : "") +
            (this.data.selected ? " selected" : "");
        this.mainHtml = <div class={bClass}>{this.data.label.text}</div>

    }
}



abstract class ControlSelect<A> {

    protected listeners: ((a: A) => void)[];
    protected select: HTMLElement;
    protected button: ControlButton;
    protected selectItems: ControlSelectItem<A>[];
    protected mainHtml: HTMLElement;
    protected selected: ControlSelectItem<A>;

    constructor(protected icon: IconName, data: SelectItemData<A>[]) {
        this.listeners = [];
        this.initHTML(data);
    }

    public onChange(listener: (s: A) => void) {
        this.listeners.push(listener);
    }

    public toggle(show: boolean): void {
        if (show) {
            this.show();
        } else {
            this.hide();
        }
    }
    
    private hide(): void {
        this.button.html.classList.add("hidden");
    }

    private show(): void {
        this.button.html.classList.remove("hidden");
    }

    protected toggleSelect() {
        this.select.classList.toggle(`${PARTHENOPE_GLOBAL_CSS_PREFIX}-select--active`);
    }

    public closeSelect() {
        this.select.classList.remove(`${PARTHENOPE_GLOBAL_CSS_PREFIX}-select--active`);
    }

    public abstract updateItems(data: SelectItemData<A>[])
    public updateSelectedItem(data: SelectItemData<A>) {
        const item = new ControlSelectItem(data);
        this.selectItem(item);
    }

    private selectItem(item: ControlSelectItem<A>): void {
        this.selected = item;
        this.selectItems.forEach(e => {
            e.selected(e.value == item.value);
        });
        this.afterItemSelect(item);
    }

    protected onItemSelect(item: ControlSelectItem<A>): void {
        this.listeners.forEach(fun => {
            fun(item.value);
        });
    }

    get html(): HTMLElement {
        return this.mainHtml;
    }

    abstract initHTML(data: SelectItemData<A>[])

    abstract afterItemSelect(item: ControlSelectItem<A>): void
}


export class ControlSelectInline<A> extends ControlSelect<A> {

    constructor(protected icon: IconName, data: SelectItemData<A>[]) {
        super(icon, data)
    }

    initHTML(data: SelectItemData<A>[]) {
        this.button = new ControlButton(this.icon);
        this.mainHtml = <div class={`${PARTHENOPE_GLOBAL_CSS_PREFIX}-select__wrap ${PARTHENOPE_GLOBAL_CSS_PREFIX}-select__wrap--${this.icon}`}>
            {this.button.html}
            <div class={`${PARTHENOPE_GLOBAL_CSS_PREFIX}-select`} ref={(el: HTMLElement) => this.select = el} ></div>
        </div>
        this.updateItems(data);
        this.button.onClick((evt) => {
            this.toggleSelect();
        });
    }

    afterItemSelect(item: ControlSelectItem<A>): void {
        this.closeSelect();
        this.button.setDecorator(item.label.decorator);
    }

    public updateItems(data: SelectItemData<A>[]) {
        if (this.selectItems && this.selectItems.length > 0) {
            this.selectItems.forEach(e => e.html.remove());
        }
        if (data) {
            let alreadySelected = false;
            this.selectItems = data.map(d => {
                d.selected = d.selected && !alreadySelected;
                const item = new ControlSelectItem(d);
                if (item.selected()) {
                    alreadySelected = true;
                    this.button.setDecorator(this.selected?.label.decorator)
                    this.selected = item;
                };
                this.select.appendChild(item.html);
                item.onSelect((i) => this.onItemSelect(i));
                return item;
            })

        }
        else this.selectItems = [];

    }

}


export class ControlSelectModal<A> extends ControlSelect<A> implements ModalContainer {
    chosen: HTMLElement;
    modalContent: HTMLElement;


    constructor(protected icon: IconName, data: SelectItemData<A>[], public modal: Modal, private label: string) {
        super(icon, data)
    }


    private buildModalContent() {
        this.modalContent = <>
            <div class={`${PARTHENOPE_GLOBAL_CSS_PREFIX}-select`} ref={(el: HTMLElement) => this.select = el}></div>
        </>

        this.selectItems.forEach(item => {
            this.select.appendChild(item.html);
        })
    }

    initHTML(data: SelectItemData<A>[]) {
        this.button = new ControlButton(this.icon);
        this.mainHtml = this.button.html;


        this.button.onClick((evt) => {
            this.buildModalContent();
            this.modal.toggle(this.label, this.modalContent);
        })
    }

    afterItemSelect(item: ControlSelectItem<A>): void {
        if(this.modal.isOpen)
            this.modal.close();
        
        this.updateChosen(item);
        this.button.setDecorator(item.label.decorator);
    }

    private updateChosen(item: ControlSelectItem<A>): void {
        this.chosen = DOMUtils.clone(item.html);
        this.chosen.classList.remove("selected");
    }

    public updateItems(data: SelectItemData<A>[]) {
        if (this.selectItems && this.selectItems.length > 0) {
            this.selectItems.forEach(e => e.html.remove());
        }

        if (data) {
            let alreadySelected = false;
            this.selectItems = data.map(d => {
                d.selected = d.selected && !alreadySelected;
                const item = new ControlSelectItem(d);
                if (item.selected()) {
                    alreadySelected = true;
                    this.selected = item;
                    this.button.setDecorator(this.selected?.label.decorator)
                    this.updateChosen(item);
                };

                item.onSelect((i) => this.onItemSelect(i));
                return item;
            })

        }
        else this.selectItems = [];
    }

}



export function selectItemForSettings(fmt: ParsedFormat, selected: boolean): SelectItemData<ParsedFormat> {

    let decorator: ItemLabelDecorator = null;
    switch (fmt.label) {
        case GenericFormatLabel.p2160:
            decorator = ItemLabelDecorator.q_4k;
            break;
        case GenericFormatLabel.p1080:
            decorator = ItemLabelDecorator.q_HD;
            break;
        default:
            break;
    }
    return {
        label: {
            text: fmt.label,
            decorator: decorator
        },
        value: fmt,
        selected: selected
    }

}

export function selectItemsForSettings(fmts: ParsedFormat[], currentQuality: ParsedFormat): SelectItemData<ParsedFormat>[] {

    return fmts.map(fmt => selectItemForSettings(fmt, fmt.label == currentQuality.label));

}




// hard coded subs 
export function selectItemsForSubs(subs: string[], currentSub: string): SelectItemData<string>[] {

    return subs.map(sub => {
        return {
            label: {
                text: sub,
            },
            value: sub,
            selected: sub == currentSub
        }
    });

}